// Thin fetch wrapper for /api/admin/roles endpoints.
// No business logic — just HTTP calls via the admin httpClient.

import {
  adminGet,
  adminPost,
  adminPatch,
  adminDelete,
} from "@/data/httpClient";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";
import type {
  Role,
  ReconcileSummary,
  AliasFailure,
  RoleUser,
  AuditEntry,
  ValidateRoleResult,
} from "@/types/role";
import type { Rule } from "@/types/rule";

export interface RoleClient {
  list(params: {
    cursor?: string;
    limit?: number;
    search?: string;
    type?: "system" | "custom";
  }): Promise<{ items: Role[]; next_cursor?: string }>;

  get(roleId: string): Promise<Role & { users_count: number; rules_count: number }>;

  create(req: {
    role_id: string;
    display_name: string;
    description: string;
    permission_set: string[];
  }): Promise<Role>;

  update(
    roleId: string,
    patch: Partial<Pick<Role, "display_name" | "description" | "permission_set">>,
    version: number,
  ): Promise<{ role: Role; summary: ReconcileSummary; partial?: AliasFailure[] }>;

  delete(
    roleId: string,
    version: number,
  ): Promise<{ role_id: string; deleted_at: string }>;

  validate(
    permission_set: string[],
  ): Promise<ValidateRoleResult>;

  reassignUsers(
    fromRoleId: string,
    toRoleId: string,
    grantFilter?: { location_id?: string },
  ): Promise<{ summary: ReconcileSummary; partial?: AliasFailure[] }>;

  reconcile(
    roleId: string,
  ): Promise<{ summary: ReconcileSummary; partial?: AliasFailure[] }>;

  listUsers(
    roleId: string,
    cursor?: string,
  ): Promise<{ items: RoleUser[]; next_cursor?: string }>;

  listRules(roleId: string): Promise<{ items: Rule[] }>;

  listActivity(
    roleId: string,
    cursor?: string,
  ): Promise<{ items: AuditEntry[]; next_cursor?: string }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    (pair): pair is [string, string | number | boolean] => pair[1] !== undefined,
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
  return `?${qs}`;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

function createRoleClient(): RoleClient {
  const BASE = "/api/admin/roles";

  return {
    list(params) {
      const qs = buildQuery(params);
      return adminGet<{ roles?: Role[]; items?: Role[]; next_cursor?: string }>(
        `${BASE}${qs}`,
      ).then((resp) => ({
        items: resp.roles ?? resp.items ?? [],
        next_cursor: resp.next_cursor,
      }));
    },

    get(roleId) {
      // Single-resource GET is enveloped as {"role": {...}} (incl.
      // users_count / rules_count). Unwrap so callers see a bare Role.
      return adminGet<Role & { users_count: number; rules_count: number }>(
        `${BASE}/${encodeURIComponent(roleId)}`,
      ).then((resp) => unwrapEnvelope(resp, "role"));
    },

    create(req) {
      // POST returns the created role enveloped as {"role": {...}}.
      return adminPost<Role>(BASE, req).then((resp) =>
        unwrapEnvelope(resp, "role"),
      );
    },

    update(roleId, patch, version) {
      // PATCH returns {"role": {...}} — no reconcile summary on the wire today.
      // Callers read `result.role` and `result.summary`, so re-shape into the
      // {role, summary} envelope and default the summary to zeros when absent.
      return adminPatch<
        | { role: Role; summary: ReconcileSummary; partial?: AliasFailure[] }
        | (Role & { summary?: ReconcileSummary })
      >(`${BASE}/${encodeURIComponent(roleId)}`, patch, version).then(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (resp: any) => {
          const role: Role = resp.role ? resp.role : resp;
          const summary: ReconcileSummary = resp.summary ?? {
            grants_added: 0,
            grants_removed: 0,
            failures_count: 0,
          };
          return { role, summary, partial: resp.partial };
        },
      );
    },

    delete(roleId, version) {
      return adminDelete(
        `${BASE}/${encodeURIComponent(roleId)}`,
        version,
      );
    },

    validate(permission_set) {
      return adminPost(`${BASE}:validate`, { permission_set });
    },

    reassignUsers(fromRoleId, toRoleId, grantFilter) {
      return adminPost(
        `${BASE}/${encodeURIComponent(fromRoleId)}:reassign-users`,
        { to_role_id: toRoleId, ...grantFilter },
      );
    },

    reconcile(roleId) {
      return adminPost(
        `${BASE}/${encodeURIComponent(roleId)}/reconcile`,
      );
    },

    listUsers(roleId, cursor) {
      const qs = buildQuery({ cursor });
      return adminGet<{ items?: RoleUser[]; users?: RoleUser[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(roleId)}/users${qs}`,
      ).then((resp) => ({
        items: resp.items ?? resp.users ?? [],
        next_cursor: resp.next_cursor,
      }));
    },

    listRules(roleId) {
      return adminGet(
        `${BASE}/${encodeURIComponent(roleId)}/rules`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ).then((resp: any) => {
        // The list response carries v0.2-shaped rules; tolerate the older
        // flat-field encoding for back-compat with pre-v0.2 BFF builds.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const raw: any[] = resp.items ?? resp.rules ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const items = raw.map((r: any) => {
          const rule = { ...r };
          if (
            !rule.group_match &&
            (rule.group_match_type || rule.group_match_group_name)
          ) {
            rule.group_match = {
              type: rule.group_match_type ?? "exact",
              group_name: rule.group_match_group_name ?? "",
            };
          }
          if (!rule.location_scope && rule.location_scope_mode) {
            const mode = rule.location_scope_mode;
            if (mode === "all_tenant_locations" || mode === "ALL") {
              rule.location_scope = { mode: "ALL", ids: [] };
            } else if (mode === "fixed_list" || mode === "ALLOW") {
              rule.location_scope = {
                mode: "ALLOW",
                ids: rule.location_scope_location_ids ?? [],
              };
            } else {
              rule.location_scope = { mode, ids: [] };
            }
          }
          if (!rule.device_scope) {
            rule.device_scope = { mode: "ALL", ids: [] };
          }
          return rule;
        });
        return { items };
      });
    },

    listActivity(roleId, cursor) {
      const qs = buildQuery({ cursor });
      return adminGet<{ items?: AuditEntry[]; activity?: AuditEntry[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(roleId)}/activity${qs}`,
      ).then((resp) => ({
        items: resp.items ?? resp.activity ?? [],
        next_cursor: resp.next_cursor,
      }));
    },
  };
}

/** Singleton role client instance. */
export const roleClient: RoleClient = createRoleClient();
